import { Form, Button } from '@arco-design/web-react'
import styles from './style/index.module.less'
import useLocale from '@/utils/useLocale'
import { useRouter } from 'next/router'

import IconGoogle from '@/assets/google.svg'
import { IconWechat } from '@arco-design/web-react/icon'
import useFormLogin from '@/hooks/useFormLogin'
import CustomInput from '@/components/CustomInput'
import Link from 'next/link'
import useGoogleAuth from '@/hooks/useGoogleAuth'
import { setToken } from '@/utils/checkLogin'
import { BindAuthRes } from '@/types/api'
import { useEffect, useState } from 'react'
import WeChatLogin from './wechat-login'
import MobileLangSwitch from '@/mobile-components/MobileLangSwitch'
import useAfterLogin from '@/hooks/useAfterLogin'
import useAfterInformation from '@/hooks/useAfterOnboarding'
import LoginBtn from '@/pages/login/login-btn'
import { Space } from 'antd-mobile'
import { emailFormValidator } from '@/utils'
import PhoneLogin from './phone-login'
import useLang from '@/utils/useLang'

const initialValues = { email: '', password: '' }

const Login = () => {
  const router = useRouter()
  const { query } = router
  const { f = '' } = query || {}
  const t = useLocale()
  const [form] = Form.useForm()
  const [showWechat, setShowWechat] = useState(false)
  const [sessionFlag, setSessionFlag] = useState('')
  const [inviteCode, setInviteCode] = useState('')
  const lang = useLang()
  const isEnglish = lang === 'en-US'
  const isChinese = lang === 'zh-CN'

  const [componentType, setComponentType] = useState<'email' | 'phone'>(isEnglish ? 'email' : 'phone')

  const { onSubmitClick, loading } = useFormLogin({
    form
  })

  useEffect(() => {
    const curInviteCode = f || localStorage.getItem('invite_code') || ''
    setInviteCode(curInviteCode as string)
  }, [f])

  const { afterLoginSuccess } = useAfterLogin({ rememberPassword: false })
  const { afterRegisterSuccess } = useAfterInformation({ rememberPassword: false })

  const onBindSuccess = (data: BindAuthRes) => {
    setToken(data?.access_token)
    if (!data?.user?.role) {
      afterRegisterSuccess()
    } else {
      afterLoginSuccess()
    }
  }

  const { googleAuth, isLoading } = useGoogleAuth({
    onBindSuccess,
    invite_code: inviteCode
  })

  if (showWechat) {
    return (
      <WeChatLogin
        type="login"
        setShowWechat={setShowWechat}
        step=""
        sessionFlag={sessionFlag}
        setSessionFlag={setSessionFlag}
        inviteCode={inviteCode}
      />
    )
  }

  return (
    <>
      <div id="captcha-element"></div>
      <div className={styles.container}>
        <div className={styles.lang}>
          <Link href="/board">{t['menu.platform.board']}</Link>
          <MobileLangSwitch />
        </div>
        <div className={styles.title}>{t['login.form.title']}</div>

        <Space direction="vertical">
          <LoginBtn
            key="google"
            icon={<IconGoogle />}
            text={t['login.google']}
            onClick={() => {
              googleAuth()
            }}
          />

          <LoginBtn
            key="wechat"
            icon={<IconWechat style={{ color: '#00C800', width: '24px', height: '24px' }} />}
            text={t['login.wechat']}
            onClick={() => {
              setShowWechat(true)
            }}
          />
        </Space>

        <div className={styles['or-wrap']}>
          <div className={styles['or-line']}></div>
          <div className={styles['or']}>{t['register.or']}</div>
          <div className={styles['or-line']}></div>
        </div>
        {componentType === 'email' ? (
          <>
            <Form form={form} layout="vertical" initialValues={initialValues}>
              <Form.Item
                label={t['login.form.email.placeholder']}
                field="email"
                rules={[{ required: true, validator: emailFormValidator, message: t['login.form.email.errMsg'] }]}
              >
                <CustomInput type="email" placeholder={t['login.form.email.placeholder']} />
              </Form.Item>
              <Form.Item
                label={t['login.form.password.label']}
                field="password"
                rules={[{ required: true, message: t['login.form.password.errMsg'] }]}
              >
                <CustomInput type="password" placeholder={t['login.form.password.placeholder']} />
              </Form.Item>

              <div className={styles['forget']}>
                <div
                  className={styles.phoneLogin}
                  onClick={() => {
                    setComponentType('phone')
                  }}
                >
                  {t['phone_login']}
                </div>
                <Link href="/forgot-password">{t['login.form.forgetPassword']}</Link>
              </div>
              <Button
                type="primary"
                long
                id="captcha-button"
                loading={loading}
                className={styles['btn']}
                onClick={onSubmitClick}
              >
                {t['login.form.login']}
              </Button>
            </Form>
            <div className={styles['no-account']}>
              {t['login.no.account']}
              <span
                className={styles['no-account-text']}
                onClick={() => {
                  router.push('/register')
                }}
              >
                {t['login.signUp']}
              </span>
            </div>
          </>
        ) : (
          <PhoneLogin
            onChangeForm={(v) => {
              setComponentType(v)
            }}
          />
        )}
      </div>
    </>
  )
}

export default Login
